
import JobPageOverview from "@/components/job/jobpage/JobPageOverview"
import Button from "@/components/base/Button"
import JobPublicContactPerson from "@/components/job/JobPublicContactPerson"
import JobPopup from "@/components/job/jobpage/JobPopup"
import JobContainer from "@/components/job/jobpage/JobContainer"
import EmojiRatingSlider from "@/components/base/input/EmojiRatingSlider"
import JobPageBaseInfoBlocks from "@/components/job/jobpage/JobPageBaseInfoBlocks"
import { mdiChevronLeft, mdiClose } from "@mdi/js"

import LazyHydrate from "vue-lazy-hydration"

import intersectionObserver from "@/mixins/intersectionObserver"
import media from "@/mixins/media"
import themes from "@/mixins/themes"
import jobFeatures from "@/mixins/jobFeatures"
import {
    getStorageData,
    setStorageData,
    LS_FIRST_TIME_USER,
} from "@/helpers/localStorage"
import { mapGetters } from "vuex"

import { Search } from "@/models/search"
import { NUM_OF_SECTION_TEMPLATES } from "@/components/job/jobpage/sections/sectionHelpers"

const cloneDeep = require("lodash.clonedeep")

export default {
    name: "JobPageAusbildung",
    components: {
        JobPageOverview,
        JobPopup,
        JobPublicContactPerson,
        JobContainer,
        Button,
        EmojiRatingSlider,
        LazyHydrate,
        JobPageBaseInfoBlocks,
        JobCardFlat: () => import("@/components/job/card/JobCardFlat"),
        DynamicContent: () => import("@/components/job/dynamic/DynamicContent"),
        ShareSpeedModal: () => import("@/components/base/ShareSpeedModal"),
        JobApprenticeshipDatePicker: () =>
            import("@/components/job/jobpage/JobApprenticeshipDatePicker"),
        JobTraineeshipDatePicker: () =>
            import("@/components/job/jobpage/JobTraineeshipDatePicker"),
        JobTraineeshipSection: () =>
            import("@/components/job/jobpage/JobTraineeshipSection"),
        JobPageRecommendations: () =>
            import("@/components/job/jobpage/JobPageRecommendations"),
        JobPoweredByOabat: () => import("@/components/job/JobPoweredByOabat"),
        JobTitleGallery: () =>
            import("@/components/job/jobpage/JobTitleGallery"),
    },
    mixins: [intersectionObserver, media, themes, jobFeatures],
    props: {
        /** Job object */
        job: {
            type: Object,
            required: true,
        },
        /** Recommendations */
        recommendations: {
            type: Object,
            default: null,
        },
        /** hide chat button  */
        hideChat: {
            type: Boolean,
            default: false,
        },
        /** hide interest button */
        alreadyRequested: {
            type: Boolean,
            default: false,
        },
        /** If component is used in editor */
        editorMode: {
            type: Boolean,
            default: false,
        },
    },
    provide() {
        return {
            editorMode: this.editorMode,
            job: () => this.job,
        }
    },
    emits: [
        "interested",
        "chat",
        "interested-apprenticeship",
        "interested-traineeship",
    ],
    data: () => ({
        // Helps to hide/display share teilen button on mobile scroll
        lastScroll: 0,
        hideShareButton: false,
        // Helps to hide/display the apply & info buttons
        numberOfMountedApplyEls: 0,
        // Popup models
        applyPopupModel: false,
        infoPopupModel: false,
        showInfoPopup: true,
        showApplyPopup: true,
        loadingBack: false,
        // First time user for badge display
        firstTimeUser: false,
    }),
    icons: {
        mdiChevronLeft,
        mdiClose,
    },
    computed: {
        ...mapGetters("routing", ["previousRoute"]),
        ...mapGetters("meta", ["meta"]),
        isApprenticeship() {
            return this.job?.type_id === 1
        },
        isTraineeship() {
            return this.job?.type_id === 2
        },
        hasTraineeship() {
            return this.job.traineeship_data?.is_active
        },
        titleMedia() {
            if (this.featureTitleGallery) {
                let galleryBlock = this.job.content_blocks?.filter(
                    (i) => i.component === "ContentBlockGallery"
                )
                if (galleryBlock?.length > 0) {
                    return galleryBlock[0].data.media
                }
                return this.getTitleMedia(this.blockDataItems)
            } else if (this.featureTitleImage || this.featureTeaser) {
                return this.getTitleImageOrVideo()
            }
            return []
        },
        blockDataItems() {
            let contentDataItems = this.job.content_blocks?.at(0)?.data.items
            if (!contentDataItems) return []
            contentDataItems = cloneDeep(contentDataItems)
            contentDataItems = contentDataItems.map((item, idx) => {
                if (!item.data.config) item.data.config = {}
                if (!item.data.config.id)
                    item.data.config.id = (idx % NUM_OF_SECTION_TEMPLATES) + 1
                if (!this.featureTeaser) {
                    item.data.media = []
                    item.data.videos = []
                    item.data.images = []
                }
                return item
            })
            return contentDataItems
        },
        dynamicBlocks() {
            return [
                {
                    component: "ContentBlockList",
                    data: {
                        show_gallery: true,
                        items: this.blockDataItems,
                    },
                },
            ]
        },
        showRecommendations() {
            return this.recommendations && this.recommendations.data.length
        },
        enableApplyPopup() {
            if (!this.isApprenticeship) return false
            if (this.editorMode) return true
            return !this.alreadyRequested && this.$can.sendInterest()
        },
        theme() {
            return this.job.theme?.theme
        },
        themeAllowed() {
            return (
                this.theme?.config?.type === "default" ||
                this.featureTheme ||
                this.editorMode
            )
        },
        shareUrl() {
            return this.$oabalytics.campaignUrl(
                this.$config.baseURL + this.$route.fullPath,
                {
                    source: "share",
                    campaignId: "job_" + this.job.id,
                }
            )
        },
    },
    watch: {
        theme: {
            handler(newTheme) {
                if (this.themeAllowed && newTheme) this.setTheme(newTheme)
                else this.setThemeDefault()
            },
            immediate: true,
        },
    },
    mounted() {
        // show badge
        this.firstTimeUser = getStorageData(LS_FIRST_TIME_USER, true)
    },
    methods: {
        onApplySectionMounted() {
            // Track number of mounted apply elements for hiding/displaying the apply & info buttons
            if (
                (this.numberOfMountedApplyEls < 1 && this.hasTraineeship) ||
                (this.numberOfMountedApplyEls < 2 && this.hasTraineeship)
            )
                this.numberOfMountedApplyEls += 1
            else {
                const contactPersonEl = document
                    .getElementsByClassName("job-public-person")
                    ?.item(0)
                const datePickerEl = document
                    .getElementsByClassName("job-page-ausbildung__datepicker")
                    ?.item(0)

                let currentVisibleEnts = new Set()

                this.observeIntersection(
                    [datePickerEl, contactPersonEl],
                    (ent) => {
                        currentVisibleEnts.add(ent.target)
                        this.infoPopupModel = false
                        this.showInfoPopup = false
                        this.applyPopupModel = false
                        this.showApplyPopup = false
                    },
                    (ent) => {
                        currentVisibleEnts.delete(ent.target)
                        if (currentVisibleEnts.size === 0) {
                            this.showApplyPopup = true
                            this.showInfoPopup = true
                        }
                    },
                    { threshold: 0.3 }
                )
            }
        },
        getTitleMedia(blockDataItems) {
            let titleMedia = this.getTitleImageOrVideo()
            if (this.featureTitleGallery) {
                let mediaCompare = (a, b) => {
                    const a_video = a.type === "video" ? 1 : 0
                    const b_video = b.type === "video" ? 1 : 0
                    return b_video - a_video
                }
                let contentBlockMedia = blockDataItems
                    ?.filter((i) => i.component !== "ContentBlockGallery")
                    .flatMap((item) => {
                        return (
                            item.data.media ||
                            item.data.images?.concat(item.data.videos)
                        )
                    })
                if (contentBlockMedia)
                    titleMedia = titleMedia.concat(contentBlockMedia)
                titleMedia = titleMedia.sort(mediaCompare).slice(0, 5)
            }
            return titleMedia
        },
        getTitleImageOrVideo() {
            let titleMedia = []
            if (this.featureTitleImage || this.featureTeaser) {
                if (this.job.video) titleMedia.push(this.job.video)
                else if (this.job.image) titleMedia.push(this.job.image)
            }
            return titleMedia
        },
        onScroll() {
            const currentScroll = window.scrollY
            const diff = currentScroll - this.lastScroll
            if (diff > 20 && currentScroll > 50) {
                this.hideShareButton = true
            } else if (diff < -20 || currentScroll < 50) {
                this.hideShareButton = false
            }
            this.lastScroll = currentScroll
        },
        onShowMore(media) {
            this.$fullscreenMedia.open(media)
        },
        goBack() {
            if (this.loadingBack) return
            this.loadingBack = true
            if (this.previousRoute) {
                this.$store.dispatch("routing/back")
                this.loadingBack = false
                return
            }

            const businessPrimaryAddress = this.job.business?.primary_address

            if (businessPrimaryAddress) {
                const route = new Search({
                    type: "apprenticeship",
                    location: {
                        slug: "nearby",
                        name: "Umgebung",
                        longitude: businessPrimaryAddress.lng,
                        latitude: businessPrimaryAddress.lat,
                    },
                }).getRoute()
                this.$router.push(route)
                this.loadingBack = false
                return
            }
            this.$router.push("/")
            this.loadingBack = false
        },
        memorizeUser() {
            // Set firstTimeUser to false (no longer first time user for 3 months)
            let expiryDate = new Date()
            expiryDate.setMonth(expiryDate.getMonth() + 3)
            setStorageData(LS_FIRST_TIME_USER, false, expiryDate.getTime())
            this.firstTimeUser = false
        },
        /** Remove badge and memorize user on the first modal open */
        onPopupApplyUpdate(newVal) {
            if (newVal && this.firstTimeUser) {
                this.memorizeUser()
            }
            this.applyPopupModel = newVal
        },
        scrollToTop() {
            window.scrollTo({ top: 0, behavior: "smooth" })
        },
    },
}
